import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Banknote, BookUser, CreditCard, Gift, Globe, Search, Utensils, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import apiClient from '@/api/client'
import { cn } from '@/lib/utils'
import {
  colorForOrderTypeId,
  displayLabelForOrderType,
  normalizeOrderTypeId,
  type OrderTypeConfig,
} from '@/lib/orderTypeSettings'
import type { OrdersBrowserPaymentFilter, OrdersBrowserStatusFilter } from '@/types'
import {
  activeFilterCount,
  clearFilters,
  type OrdersBrowserFilters,
} from './ordersBrowserFilters'

export type PraFilter = 'all' | 'printed' | 'not_printed' | 'eligible'
export type FiscalFilter = 'all' | 'synced' | 'pending' | 'failed' | 'exempt' | 'none'

const PRA_FILTERS: { id: PraFilter; label: string; hint: string }[] = [
  { id: 'all', label: 'All', hint: 'Every order' },
  { id: 'not_printed', label: 'Not printed', hint: 'PRA invoice was never printed for these' },
  { id: 'printed', label: 'Printed', hint: 'A PRA invoice was already issued' },
  { id: 'eligible', label: 'Eligible', hint: 'You can issue / reissue a PRA invoice now' },
]

const FISCAL_FILTERS: { id: FiscalFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'synced', label: 'Synced' },
  { id: 'pending', label: 'Pending' },
  { id: 'failed', label: 'Failed' },
  // F-MM-N23: marketplace-settled orders — never filed, by design.
  { id: 'exempt', label: 'Exempt' },
  { id: 'none', label: 'N/A' },
]

const PAYMENTS: { id: OrdersBrowserPaymentFilter; label: string; icon?: JSX.Element }[] = [
  { id: '', label: 'Any tender' },
  { id: 'cash', label: 'Cash', icon: <Banknote className="h-3.5 w-3.5" /> },
  { id: 'card', label: 'Card', icon: <CreditCard className="h-3.5 w-3.5" /> },
  { id: 'online', label: 'Online', icon: <Globe className="h-3.5 w-3.5" /> },
  // F-MM-N24: without this chip a gift-card bill was reachable only by URL or
  // by drilling from the Tender mix — and "which bills used a gift card?" is
  // exactly what gets asked when a guest queries their balance.
  { id: 'gift_card', label: 'Gift card', icon: <Gift className="h-3.5 w-3.5" /> },
  // F-MM-N30: staff meals are listed (stock moved) but excluded from totals.
  { id: 'tab', label: 'Tab', icon: <BookUser className="h-3.5 w-3.5" /> },
  { id: 'staff_meal', label: 'Staff meal', icon: <Utensils className="h-3.5 w-3.5" /> },
]

const STATUSES: { id: OrdersBrowserStatusFilter; label: string; hint: string }[] = [
  { id: '', label: 'Any status', hint: 'Completed, refunded, cancelled and open orders' },
  { id: 'completed', label: 'Completed', hint: 'Paid and closed, not refunded' },
  { id: 'credit_noted', label: 'Refunded', hint: 'Cancelled after payment with a credit note / refund' },
  { id: 'cancelled', label: 'Cancelled', hint: 'Voided before payment' },
  { id: 'open', label: 'Open', hint: 'Still on the floor or in the kitchen' },
]

interface Props {
  filters: OrdersBrowserFilters
  onChange: (next: OrdersBrowserFilters) => void
  /** Tenant order-type config — labels, colours and the chip order. */
  orderTypeRows: OrderTypeConfig[]
  /** Order-type ids seen in loaded rows but absent from config (disabled /
   *  legacy types). They still get a chip so history stays reachable. */
  observedOrderTypeIds: Iterable<string>

  search: string
  onSearchChange: (v: string) => void

  praEnabled: boolean
  praFilter: PraFilter
  onPraFilterChange: (v: PraFilter) => void
  fiscalEnabled: boolean
  fiscalFilter: FiscalFilter
  onFiscalFilterChange: (v: FiscalFilter) => void

  emptyCancelCount: number
  showEmptyCancels: boolean
  onToggleEmptyCancels: () => void

  windowDays: number
  /** Right-aligned slot (export button etc). */
  trailing?: React.ReactNode
}

/**
 * F-RPT-N30 — the facet row above the Orders table. Order type, tender (with
 * the online provider under it), status, then the PRA / FBR groups the
 * fiscal tenants already had. Every control writes straight into `filters`;
 * the owner mirrors that into the URL.
 */
export function OrdersFilterBar({
  filters,
  onChange,
  orderTypeRows,
  observedOrderTypeIds,
  search,
  onSearchChange,
  praEnabled,
  praFilter,
  onPraFilterChange,
  fiscalEnabled,
  fiscalFilter,
  onFiscalFilterChange,
  emptyCancelCount,
  showEmptyCancels,
  onToggleEmptyCancels,
  windowDays,
  trailing,
}: Props) {
  // Settings order first (that's the order the counter shows them in), then
  // any id present in the data but absent from settings, alphabetically.
  const orderTypeOptions = useMemo(() => {
    const configured = orderTypeRows
      .filter((r) => r.enabled !== false)
      .map((r) => normalizeOrderTypeId(r.id))
    const pool = new Set<string>(observedOrderTypeIds)
    filters.orderTypes.forEach((id) => pool.add(id))
    const out: string[] = []
    for (const id of configured) {
      out.push(id)
      pool.delete(id)
    }
    return [...out, ...Array.from(pool).sort()]
  }, [orderTypeRows, observedOrderTypeIds, filters.orderTypes])

  const subMethodsQuery = useQuery({
    queryKey: ['payment-submethods'],
    queryFn: () => apiClient.getOnlineSubMethods(),
    staleTime: 60_000,
    enabled: filters.payment === 'online',
  })
  const subMethods = useMemo(() => {
    const cfg = subMethodsQuery.data?.data
    const methods = cfg?.methods ?? []
    // Keep a provider that was removed after its sales were recorded, so an
    // old deep-link still resolves.
    if (filters.subMethod && !methods.some((m) => m.slug === filters.subMethod)) {
      return [...methods, { slug: filters.subMethod, label: filters.subMethod, is_active: false, sort_order: 999 }]
    }
    return methods
  }, [subMethodsQuery.data, filters.subMethod])

  const toggleOrderType = (id: string) => {
    const has = filters.orderTypes.includes(id)
    onChange({
      ...filters,
      orderTypes: has ? filters.orderTypes.filter((x) => x !== id) : [...filters.orderTypes, id],
    })
  }

  const activeCount = activeFilterCount(filters)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search order #, table, customer, server"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 h-9"
          />
        </div>

        <Segmented
          value={filters.payment}
          options={PAYMENTS}
          onChange={(v) => onChange({ ...filters, payment: v, subMethod: v === 'online' ? filters.subMethod : '' })}
        />

        <Segmented
          value={filters.status}
          options={STATUSES}
          onChange={(v) => onChange({ ...filters, status: v })}
        />

        {activeCount > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1 text-xs"
            onClick={() => onChange(clearFilters(filters))}
          >
            <X className="h-3.5 w-3.5" />
            Clear filters
            <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">
              {activeCount}
            </Badge>
          </Button>
        )}

        <div className="ml-auto flex items-center gap-2">
          {trailing}
          <Badge variant="outline" className="text-[11px]">
            Late-print window: {windowDays} day{windowDays === 1 ? '' : 's'}
          </Badge>
        </div>
      </div>

      {orderTypeOptions.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Order type</span>
          <div className="inline-flex flex-wrap items-center gap-1.5">
            <Chip active={filters.orderTypes.length === 0} onClick={() => onChange({ ...filters, orderTypes: [] })}>
              All
            </Chip>
            {orderTypeOptions.map((id) => {
              const color = colorForOrderTypeId(orderTypeRows, id)
              return (
                <Chip key={id} active={filters.orderTypes.includes(id)} onClick={() => toggleOrderType(id)}>
                  {color && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} aria-hidden />}
                  {displayLabelForOrderType(id, orderTypeRows)}
                </Chip>
              )
            })}
          </div>
        </div>
      )}

      {filters.payment === 'online' && subMethods.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Provider</span>
          <div className="inline-flex flex-wrap items-center gap-1.5">
            <Chip active={!filters.subMethod} onClick={() => onChange({ ...filters, subMethod: '' })}>
              Any provider
            </Chip>
            {subMethods.map((m) => (
              <Chip
                key={m.slug}
                active={filters.subMethod === m.slug}
                onClick={() => onChange({ ...filters, subMethod: filters.subMethod === m.slug ? '' : m.slug })}
              >
                {m.label}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {(praEnabled || fiscalEnabled || emptyCancelCount > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {praEnabled && (
            <div className="flex items-center gap-1 rounded-md bg-muted p-1">
              <span className="px-1.5 text-[11px] font-medium text-muted-foreground">PRA</span>
              {PRA_FILTERS.map((f) => (
                <TooltipProvider key={f.id} delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        size="sm"
                        variant={praFilter === f.id ? 'default' : 'ghost'}
                        className="h-7 px-3 text-xs"
                        onClick={() => onPraFilterChange(f.id)}
                      >
                        {f.label}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">{f.hint}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          )}

          {fiscalEnabled && (
            <div className="flex items-center gap-1 rounded-md bg-muted p-1">
              <span className="px-1.5 text-[11px] font-medium text-muted-foreground">FBR</span>
              {FISCAL_FILTERS.map((f) => (
                <Button
                  key={f.id}
                  type="button"
                  size="sm"
                  variant={fiscalFilter === f.id ? 'default' : 'ghost'}
                  className="h-7 px-3 text-xs"
                  onClick={() => onFiscalFilterChange(f.id)}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          )}

          {emptyCancelCount > 0 && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant={showEmptyCancels ? 'default' : 'outline'}
                    className="h-7 px-3 text-xs"
                    onClick={onToggleEmptyCancels}
                  >
                    Empty cancels ({emptyCancelCount})
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs max-w-xs">
                  Rs 0 Cancelled rows from releasing empty seated tables (or void-all).
                  Hidden by default — they do not affect Gross Sales.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
    </div>
  )
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: { id: T; label: string; icon?: JSX.Element; hint?: string }[]
  onChange: (v: T) => void
}) {
  return (
    <div className="flex items-center gap-1 rounded-md bg-muted/60 p-1">
      {options.map((o) => {
        const btn = (
          <button
            key={o.id}
            type="button"
            aria-pressed={value === o.id}
            onClick={() => onChange(o.id)}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 rounded px-2.5 text-xs transition',
              value === o.id
                ? 'bg-background font-medium text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {o.icon}
            {o.label}
          </button>
        )
        if (!o.hint) return btn
        return (
          <TooltipProvider key={o.id} delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>{btn}</TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">{o.hint}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      })}
    </div>
  )
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition',
        active
          ? 'border-foreground/20 bg-foreground/5 font-medium text-foreground'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}
